/**
 * Which detail layout a session renders. A session whose sources include any
 * file-based kind (a dropped flat file, spreadsheet, archive/DB dump, or a
 * documents drop) uses the flat-file import layout (FhirImportPanel); only a
 * true live-EHR-scrape session falls through to the EHR layout.
 *
 * Kept as a pure helper (not inline in SessionDetailPage) so the routing is
 * unit-tested — a regression here silently hides the import controls.
 */
export const FLAT_FILE_SOURCE_KINDS = ['csv', 'tsv', 'xlsx', 'xls', 'bulk', 'documents', 'db', 'sql'] as const;

export function isFileDropSession(sources: ReadonlyArray<{ kind?: string }> | null | undefined): boolean {
  return (sources ?? []).some((s) => (FLAT_FILE_SOURCE_KINDS as readonly string[]).includes(String(s?.kind)));
}

export type StepState = 'done' | 'current' | 'todo';

export interface ImportStepInput {
  uploadCount: number;
  acceptedSpecCount: number;
  /** ≥1 complete file→spec pairing and no half-filled row. */
  launchReady: boolean;
  isRunning: boolean;
  isDone: boolean;
}

/**
 * The guided-import step strip's per-step state, derived from real session data.
 * Pure so the workflow logic is unit-tested (FhirImportPanel just renders it).
 */
export function deriveImportSteps(i: ImportStepInput): Array<{ label: string; state: StepState }> {
  return [
    { label: 'Preview', state: i.uploadCount > 0 ? 'done' : 'todo' },
    { label: 'Map → FHIR', state: i.acceptedSpecCount > 0 ? 'done' : i.uploadCount > 0 ? 'current' : 'todo' },
    { label: 'Pair files', state: i.launchReady ? 'done' : i.acceptedSpecCount > 0 ? 'current' : 'todo' },
    { label: 'Launch', state: i.isRunning || i.isDone ? 'done' : i.launchReady ? 'current' : 'todo' },
    { label: 'Monitor', state: i.isDone ? 'done' : i.isRunning ? 'current' : 'todo' },
    { label: 'Results', state: i.isDone ? 'current' : 'todo' },
  ];
}
